import { useEffect, useState } from 'react';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import { apiClient } from '@/lib/api-client';

type FinanceCapability = {
  canOpenFinance: boolean;
  canWriteForecast: boolean;
};

export type FinanceCapabilityState =
  | { status: 'checking' }
  | { status: 'no-context' }
  | { status: 'load-error' }
  | { status: 'no-access' }
  | {
      status: 'allowed';
      canWriteForecast: boolean;
      /**
       * The Finance permission codes this caller actually holds.
       *
       * Derived from the capability body rather than guessed from the actor class, because the
       * actor class is two values and there are nine grantable codes: an accountant and a
       * managing director are both `finance-viewer` and must not see the same screens (Human
       * decision 27). The rail gates each Finance section on these.
       */
      permissions: ReadonlySet<string>;
    };

/**
 * The capability flag for each surface, and the code it means.
 *
 * A flag that the response does not carry is ABSENT rather than false-by-default — see below for
 * why that distinction is the safe one here.
 */
const SURFACE_CODES: ReadonlyArray<readonly [string, string]> = [
  ['canReadDashboard', 'finance:dashboard:read'],
  ['canReadMovements', 'finance:movements:read'],
  ['canReadIncomeStatement', 'finance:income-statement:read'],
  ['canReadCashFlow', 'finance:cash-flow:read'],
  ['canReadCommesse', 'finance:commesse:read'],
  ['canReadReferenceRegisters', 'finance:reference-registers:read'],
  ['canReadDividends', 'finance:dividends:read'],
  ['canReadPrimaNota', 'finance:prima-nota:read'],
  ['canWritePrimaNota', 'finance:prima-nota:write'],
  ['canWriteForecast', 'finance:forecast:write'],
];

/**
 * What a server that predates the per-surface split could prove by answering at all.
 *
 * Reaching `capability` used to require `finance:dashboard:read` AND `finance:movements:read`
 * together, and holding those two reached every read route in the module. So on an old server a
 * successful response IS the statement that the caller may see all of it.
 */
const LEGACY_COMPOSITE_GRANT: readonly string[] = [
  'finance:dashboard:read',
  'finance:movements:read',
  'finance:income-statement:read',
  'finance:cash-flow:read',
  'finance:commesse:read',
  'finance:reference-registers:read',
  'finance:prima-nota:read',
];

/**
 * Read the granted surfaces out of the capability body.
 *
 * TWO SERVERS HAVE TO BE HANDLED, and getting the polarity right here matters more than it looks.
 * The console and the finance service deploy independently — the release skips a service whose
 * image digest has not changed — so a console that knows about per-surface flags will meet a
 * finance service that does not send them.
 *
 * Treating an absent flag as `false` would be the reflex, and it is wrong HERE. It would empty
 * the whole Finance menu for everybody during that window, while the old server happily served
 * every one of those routes. The navigation is not the security boundary — the routes are, and
 * they enforce their own codes — so the cost of being too permissive is a menu entry that leads
 * to a refusal, and the cost of being too strict is a module that looks deleted.
 *
 * So: if the response carries NONE of the new flags it is an old server, and answering at all
 * proved the composite grant. If it carries ANY of them it is a new server and is believed
 * exactly, absences included.
 */
function grantedSurfaces(payload: Record<string, unknown>): ReadonlySet<string> {
  const speaksPerSurface = SURFACE_CODES.some(([flag]) => typeof payload[flag] === 'boolean');
  if (!speaksPerSurface) {
    const legacy = new Set<string>(LEGACY_COMPOSITE_GRANT);
    if (payload.canWriteForecast === true) {
      legacy.add('finance:forecast:write');
    }
    return legacy;
  }

  const held = new Set<string>();
  for (const [flag, code] of SURFACE_CODES) {
    if (payload[flag] === true) {
      held.add(code);
    }
  }
  return held;
}

function isFinanceCapability(value: unknown): value is FinanceCapability {
  return (
    typeof value === 'object' &&
    value !== null &&
    'canOpenFinance' in value &&
    typeof value.canOpenFinance === 'boolean' &&
    'canWriteForecast' in value &&
    typeof value.canWriteForecast === 'boolean'
  );
}

export function useFinanceCapability({
  requireActiveContext = true,
}: { requireActiveContext?: boolean } = {}): FinanceCapabilityState {
  const activeContext = useActiveContext();
  const [state, setState] = useState<FinanceCapabilityState>({ status: 'checking' });

  useEffect(() => {
    if (activeContext.status === 'loading') {
      setState({ status: 'checking' });
      return;
    }

    if (activeContext.status === 'error') {
      setState({ status: 'load-error' });
      return;
    }

    // No request without an active organization: the service answers it with 400
    // ACTIVE_CONTEXT_REQUIRED, every time. A caller that does not require a context (the shell's
    // navigation) gets 'no-access' straight away, which is what the refused request used to give.
    if (activeContext.active === null) {
      setState({ status: requireActiveContext ? 'no-context' : 'no-access' });
      return;
    }

    let cancelled = false;
    setState({ status: 'checking' });

    Promise.resolve()
      .then(() => apiClient.GET('/v1/finance/capability'))
      .then(({ data, error, response }) => {
        if (cancelled) {
          return;
        }

        const payload: unknown = data;

        if (
          error !== undefined ||
          !response.ok ||
          !isFinanceCapability(payload) ||
          !payload.canOpenFinance
        ) {
          setState({ status: 'no-access' });
          return;
        }

        setState({
          status: 'allowed',
          canWriteForecast: payload.canWriteForecast,
          permissions: grantedSurfaces(payload),
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setState({ status: 'load-error' });
      });

    return () => {
      cancelled = true;
    };
  }, [activeContext.active?.organizationId, activeContext.status, requireActiveContext]);

  return state;
}
